import get from 'lodash-es/get'
import last from 'lodash-es/last'
import * as React from 'react'
import * as titleize from 'titleize'

import {
  IAggregations,
  IFilterOption,
  INumberlessFilterOption
} from '../../../../typings/searchResults'

import filterParamsConfig from '../../../../common/constants/filterParamsConfig'
import { Site } from '../../../../common/constants/siteList'
import { ILocationParams } from '../../../../common/reducers/reactRouterLocation'
import FilterBoolean from './filterBoolean/'
import FilterCategory from './filterCategory'
import FilterMultiselect from './filterMultiselect/'
import FilterProperties from './filterProperties'
import FilterRadio from './filterRadio/filterRadio'
import FiltersRange from './filtersRange/'
import FiltersSales from './filtersSales/'
import FiltersSize from './filtersSize/'
import FilterText from './filterText/'
import SearchFilters from './searchFilters'

import FilterMinResolution from './filterMinResolution'

interface IProps {
  aggregations: IAggregations
  locationParams: ILocationParams
  currentSite: Site
  makeSearchLink: (params: ILocationParams, currentSite?: Site) => string
  totalHits: number
  loading: boolean
}

const sortAlphabetically = (options?: IFilterOption[]) => {
  return (options || []).sort((opt1, opt2) => (opt1.key < opt2.key ? -1 : 1))
}

const hasAggregation = (items: IFilterOption[] | undefined): boolean => {
  return !!items && items.filter(item => item && item.count > 0).length > 0
}

const sortByColors = (options: IFilterOption[]) => {
  const order = [
    'pink',
    'red',
    'orange',
    'yellow',
    'green',
    'teal',
    'blue',
    'purple',
    'brown',
    'black',
    'grey',
    'white'
  ]
  return orderAndFilterOptionsByOrderedKeys(options, order)
}

const sortByTempo = (options: IFilterOption[]) => {
  const order = ['very-slow', 'slow', 'medium', 'upbeat', 'fast', 'very-fast']
  return orderAndFilterOptionsByOrderedKeys(options, order)
}

const orderAndFilterOptionsByOrderedKeys = (
  options: IFilterOption[],
  orderedKeys: string[]
) => {
  return (options || [])
    .filter(option => orderedKeys.indexOf(option.key) >= 0)
    .sort(
      (opt1, opt2) =>
        orderedKeys.indexOf(opt1.key) - orderedKeys.indexOf(opt2.key)
    )
}

type FilterOptions = IFilterOption | INumberlessFilterOption

const withOption = (
  options: IFilterOption[],
  option: IFilterOption | INumberlessFilterOption
): FilterOptions[] => {
  return options ? [option, ...options] : []
}

const withShowAllOption = (options: IFilterOption[]): FilterOptions[] => {
  return withOption(options, {
    description: null,
    key: '',
    label: 'Show all'
  })
}

const withTotal = (options?: IFilterOption[]): FilterOptions[] => {
  if (!options) {
    return []
  }
  const total = options.reduce((totalValue, curr) => totalValue + curr.count, 0)

  const totalOption = {
    count: total,
    description: null,
    key: ''
  }
  return withOption(options, totalOption)
}

const withSameLabelsAsKeys = (options?: IFilterOption[]) => {
  return (options || []).map(option => ({
    ...option,
    label: option.key
  }))
}

const withCategoryLabels = (options?: IFilterOption[]) => {
  return (options || []).map(option => {
    const path = option.key.split('/')
    const name = last(path) || ''
    const label = titleize(name.replace('-', ' '))

    return {
      ...option,
      label
    }
  })
}

const SearchFiltersContainer = ({
  aggregations = {},
  locationParams,
  currentSite,
  makeSearchLink,
  totalHits,
  loading
}: IProps) => {
  const filters = [
    {
      Component: FilterCategory,
      id: 'category',
      isActive: get(aggregations, 'category.length') > 0,
      props: {
        scrollable: true,
        type: 'category',
        items: withOption(
          withCategoryLabels(aggregations.category as IFilterOption[]),
          {
            description: null,
            key: '',
            label: 'All categories',
            count: get(aggregations, 'category_root_count.count')
          }
        )
      }
    },
    {
      Component: FilterMultiselect,
      id: 'tags',
      isActive: get(aggregations, 'tags.length') > 0,
      props: {
        type: 'tags',
        items: aggregations.tags
      }
    },
    {
      Component: FiltersRange,
      id: 'price',
      isActive: get(aggregations, 'cost.sum') > 0,
      props: {
        paramName: 'price',
        name: 'price',
        type: 'number',
        prefix: '$',
        range: {
          min: get(aggregations, 'cost.min') / 100,
          max: get(aggregations, 'cost.max') / 100
        }
      }
    },
    {
      Component: FiltersSales,
      id: 'sales',
      isActive: true,
      props: {
        type: 'sales',
        items: sortAlphabetically(aggregations.sales_count)
      }
    },
    {
      Component: FilterRadio,
      id: 'rating',
      isActive: get(aggregations, 'rating.length') > 0,
      title: 'Rating',
      props: {
        paramName: 'rating_min',
        items: sortAlphabetically(aggregations.rating),
        labels: filterParamsConfig.rating_min.labels
      }
    },
    {
      Component: FilterRadio,
      id: 'orientation',
      isActive: get(aggregations, 'orientation.length') > 0,
      props: {
        paramName: 'orientation',
        items: withTotal(sortAlphabetically(aggregations.orientation)),
        labels: filterParamsConfig.orientation.labels
      }
    },
    {
      Component: FilterRadio,
      id: 'date-added',
      title: 'Date Added',
      isActive: hasAggregation(get(aggregations, 'date', [])),
      props: {
        paramName: 'date',
        items: aggregations.date,
        labels: filterParamsConfig.date.labels
      }
    },
    {
      Component: FiltersSize,
      id: 'sizes',
      isActive: get(aggregations, 'size.length') > 0,
      props: {
        items: sortAlphabetically(aggregations.size)
      }
    },
    {
      Component: FilterMultiselect,
      id: 'colors',
      isActive: get(aggregations, 'colors.length') > 0,
      props: {
        type: 'colors',
        items: sortByColors(aggregations.colors)
      }
    },
    {
      Component: FilterCategory,
      id: 'software-version',
      isActive: get(aggregations, 'platform.length') > 0,
      props: {
        type: 'platform',
        separator: ' ',
        scrollable: true,
        items: withOption(withSameLabelsAsKeys(aggregations.platform), {
          description: null,
          key: '',
          label: 'All software versions',
          count: get(aggregations, 'platform_root_count.count')
        })
      }
    },
    {
      Component: FilterMinResolution,
      id: 'resolution',
      isActive: get(aggregations, 'resolution.length') > 0,
      title: 'Minimum Resolution',
      props: {
        paramName: 'resolution_min',
        loading,
        items: withOption(aggregations.resolution, {
          description: null,
          key: '',
          label: 'All',
          count: totalHits
        })
      }
    },
    {
      Component: FilterRadio,
      id: 'frame-rate',
      isActive: get(aggregations, 'frame_rate.length') > 0,
      props: {
        paramName: 'frame_rate',
        items: withShowAllOption(aggregations.frame_rate),
        labels: filterParamsConfig.frame_rate.labels
      }
    },
    {
      Component: FilterMultiselect,
      id: 'compatible-with',
      isActive: get(aggregations, 'compatible_with.length') > 0,
      props: {
        type: 'compatible_with',
        items: aggregations.compatible_with
      }
    },
    {
      Component: FilterBoolean,
      id: 'hosting-setup',
      isActive: get(aggregations, 'hosting_setup.count') > 0,
      props: {
        type: 'hosting_setup',
        label: 'Hosting & Setup',
        count: get(aggregations, 'hosting_setup.count')
      }
    },
    {
      Component: FilterRadio,
      id: 'requires-plugins',
      isActive: get(aggregations, 'requires_plugins.length') > 0,
      title: 'Requires Plugins',
      props: {
        paramName: 'requires_plugins',
        items: withShowAllOption(aggregations.requires_plugins),
        labels: filterParamsConfig.requires_plugins.labels
      }
    },
    {
      Component: FiltersRange,
      id: 'length',
      isActive: get(aggregations, 'length.sum') > 0,
      props: {
        paramName: 'length',
        name: 'length',
        type: 'time',
        range: {
          min: get(aggregations, 'length.min'),
          max: get(aggregations, 'length.max')
        }
      }
    },
    {
      Component: FilterMultiselect,
      id: 'vocals',
      title: 'Vocals',
      isActive: get(aggregations, 'vocals_in_audio.length') > 0,
      props: {
        type: 'vocals_in_audio',
        items: aggregations.vocals_in_audio,
        labels: filterParamsConfig.vocals_in_audio.labels
      }
    },
    {
      Component: FilterRadio,
      id: 'tempo',
      isActive: get(aggregations, 'tempo.length') > 0,
      title: 'Tempo',
      props: {
        paramName: 'tempo',
        items: withShowAllOption(sortByTempo(aggregations.tempo)),
        labels: filterParamsConfig.tempo.labels
      }
    },
    {
      Component: FilterText,
      id: 'polygons',
      isActive: aggregations.poly_count,
      props: {
        paramName: 'poly_count',
        name: 'Polygons',
        placeholder: `${get(aggregations, 'poly_count.avg')}, ${get(
          aggregations,
          'poly_count.min'
        )}-${get(aggregations, 'poly_count.max')}`
      }
    },
    {
      Component: FilterProperties,
      id: 'properties',
      isActive:
        get(aggregations, 'alpha.count') > 0 ||
        get(aggregations, 'looped.count') > 0,
      title: 'Properties',
      props: {
        alphaCount: get(aggregations, 'alpha.count'),
        loopedCount: get(aggregations, 'looped.count')
      }
    },
    {
      Component: FilterRadio,
      id: 'include-pro-affiliated',
      isActive: get(aggregations, 'include_pro_affiliated.length') > 0,
      title: 'Include P.R.O Music',
      props: {
        items: get(aggregations, 'include_pro_affiliated'),
        paramName: 'include_pro_affiliated',
        labels: filterParamsConfig.include_pro_affiliated.labels,
        defaultSelectedValue: 'true'
      }
    },
    {
      Component: FilterMultiselect,
      id: 'file-formats',
      isActive: get(aggregations, 'file_formats.length') > 0,
      title: 'File Formats',
      props: {
        type: 'file_formats',
        items: aggregations.file_formats,
        labels: filterParamsConfig.file_formats.labels
      }
    }
  ]

  const activeFilters = filters.filter(filter => filter.isActive)

  return (
    <SearchFilters
      locationParams={locationParams}
      activeFilters={activeFilters}
      currentSite={currentSite}
      makeSearchLink={makeSearchLink}
    />
  )
}

export default SearchFiltersContainer
